import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CursorPage, Plant, TreatmentKind, TreatmentOutcome, TreatmentRow, TreatmentSummary } from '@dru/types';
import {
  TREATMENT_KIND_LABEL, TREATMENT_KINDS, TREATMENT_OUTCOME_LABEL, TREATMENT_OUTCOMES,
} from '@dru/types';
import { Plus } from 'lucide-react';
import { Button, DataTable, EmptyState, ErrorState, FilterBar, Input, KeyFigures, SearchInput, cn } from '@dru/ui';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { TreatmentDetailDialog, TreatmentDialog, treatmentColumns } from '../components/TreatmentRecord';
import { isPlantBound } from '../lib/billing-view';

/**
 * The treatment register.
 *
 * What the first-aid room's book becomes once it is typed in: every casualty,
 * first-aid and OPD treatment given on the premises, newest first. The
 * questions it answers are the ones an inspection or a safety committee asks
 * — how many, how many were the work's doing, who is due a follow-up — so
 * those are the figures at the top, and each is a shortcut to its rows.
 *
 * The figures come from `/treatments/summary`, counted by the server over
 * the chosen period — never from the rows loaded on this page.
 *
 * No plant filter: the top bar's plant picker already narrows every HR read
 * to one plant, and a plant login is held to its own.
 *
 * Deliberately not a medical case. Nothing here is validated or signed, and
 * a worker who needs an examination has one arranged from their record.
 */

const day = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => day(new Date(Date.now() - n * 86_400_000));

type Period = '7' | '30' | '90' | 'year' | 'all' | 'custom';
const PERIODS: Array<{ key: Period; label: string }> = [
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: 'year', label: 'This year' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];
const isPeriod = (v: string | null): v is Period => PERIODS.some((p) => p.key === v);

/** The window a period stands for, as the API's inclusive `from`/`to` days. */
function windowOf(period: Period, from: string, to: string): { from: string; to: string } {
  switch (period) {
    case '7': return { from: daysAgo(6), to: '' };
    case '30': return { from: daysAgo(29), to: '' };
    case '90': return { from: daysAgo(89), to: '' };
    case 'year': return { from: `${new Date().getFullYear()}-01-01`, to: '' };
    case 'all': return { from: '', to: '' };
    case 'custom': return { from, to };
  }
}

export function Treatments() {
  const { permissions, principal } = useAuth();
  const canRead = permissions.has('treatment.read');
  // The header switcher narrows a company login to one plant already. A
  // plant-bound login spanning several plants has no switcher, so it keeps
  // its own plant filter here.
  const canPlants = permissions.has('plant.read');
  const bound = isPlantBound(principal);
  const plants = useQuery<CursorPage<Plant> | null>(
    () => (bound && canPlants ? api.get<CursorPage<Plant>>('/plants?limit=100') : Promise.resolve(null)), [bound, canPlants]);
  const plantList = plants.data?.items ?? [];
  const showPlant = bound && plantList.length > 1;
  const canWrite = permissions.has('treatment.write');

  // Filters live in the address, so a shared link or the back button keeps them.
  const [params, setParams] = useSearchParams();
  const kind = (TREATMENT_KINDS as string[]).includes(params.get('kind') ?? '') ? params.get('kind') as TreatmentKind : '';
  const outcome = (TREATMENT_OUTCOMES as string[]).includes(params.get('outcome') ?? '') ? params.get('outcome') as TreatmentOutcome : '';
  const workRelated = params.get('work') === 'true' || params.get('work') === 'false' ? params.get('work') as 'true' | 'false' : '';
  // The last 90 days by default: the register is read for what happened
  // recently, not as an unbounded list of a large plant's first-aid visits.
  const period: Period = isPeriod(params.get('period')) ? params.get('period') as Period : '90';
  const customFrom = params.get('from') ?? '';
  const customTo = params.get('to') ?? '';
  const applied = params.get('q') ?? '';
  const plantId = showPlant ? params.get('plantId') ?? '' : '';
  const { from, to } = windowOf(period, customFrom, customTo);

  const set = (next: Record<string, string | undefined>) => setParams((p) => {
    const n = new URLSearchParams(p);
    for (const [k, v] of Object.entries(next)) { if (v) n.set(k, v); else n.delete(k); }
    return n;
  }, { replace: true });

  const [search, setSearch] = useState(applied);
  useEffect(() => {
    const t = setTimeout(() => { if (search.trim() !== applied) set({ q: search.trim() || undefined }); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, applied]);

  const [recording, setRecording] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [saved, setSaved] = useState<TreatmentRow | null>(null);

  const query = new URLSearchParams({ limit: '50' });
  if (kind) query.set('kind', kind);
  if (outcome) query.set('outcome', outcome);
  if (workRelated) query.set('workRelated', workRelated);
  if (from) query.set('from', from);
  if (to) query.set('to', to);
  if (applied) query.set('q', applied);
  if (plantId) query.set('plantId', plantId);
  const qs = query.toString();

  const page = usePages<TreatmentRow>(
    (cursor) => (canRead
      ? api.get<CursorPage<TreatmentRow>>(`/treatments?${qs}${cursor ? `&cursor=${cursor}` : ''}`)
      : Promise.resolve({ items: [], nextCursor: null })),
    [qs, canRead],
  );

  // The figures follow the period only, as the endpoint counts them; the
  // kind and outcome filters narrow the list, not the totals.
  const summaryQs = new URLSearchParams();
  if (from) summaryQs.set('from', from);
  if (to) summaryQs.set('to', to);
  if (plantId) summaryQs.set('plantId', plantId);
  const summary = useQuery<TreatmentSummary | null>(
    () => (canRead ? api.get<TreatmentSummary>(`/treatments/summary?${summaryQs.toString()}`) : Promise.resolve(null)),
    [summaryQs.toString(), canRead],
  );
  const s = summary.data;

  const reload = () => { page.reload(); summary.reload(); };

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Treatment register" />
        <EmptyState title="The treatment register is not part of your role" hint="Ask your company administrator for access." />
      </div>
    );
  }

  const periodLabel = period === 'custom'
    ? (from || to
      ? `${from ? formatDate(from) : 'the start'} – ${to ? formatDate(to) : 'today'}`
      : 'all time')
    : PERIODS.find((p) => p.key === period)!.label.toLowerCase();
  const filtered = Boolean(applied || kind || outcome || workRelated || plantId);
  const clearFilters = () => { setSearch(''); set({ kind: undefined, outcome: undefined, work: undefined, q: undefined, plantId: undefined }); };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Treatment register"
        hint="Casualty, first-aid and OPD visits to the first-aid room or OHC. A register entry only: it is not a medical examination and never a fitness decision."
        actions={canWrite && (
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setRecording(true)}>Record treatment</Button>
        )}
      />

      {saved && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-covered-100 bg-covered-100 px-4 py-2.5 text-small text-neutral-900">
          <span>
            {TREATMENT_KIND_LABEL[saved.kind]} recorded for <span className="font-medium">{saved.employee.fullName}</span>.
          </span>
          <span className="flex items-center gap-3">
            <button type="button" onClick={() => setOpenId(saved.id)} className="font-medium underline-offset-2 hover:underline max-md:min-h-10">
              Open it
            </button>
            <button type="button" onClick={() => setSaved(null)} className="text-neutral-600 hover:underline max-md:min-h-10" aria-label="Dismiss">
              Dismiss
            </button>
          </span>
        </div>
      )}

      {/* ── The period every figure and row below covers ─────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Period" className="flex gap-1.5 max-md:-mx-4 max-md:overflow-x-auto max-md:px-4 max-md:pb-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={period === p.key}
              onClick={() => set({ period: p.key === '90' ? undefined : p.key, ...(p.key === 'custom' ? {} : { from: undefined, to: undefined }) })}
              className={cn(
                'h-8 shrink-0 rounded-full border px-3 text-small font-medium transition-colors duration-hover max-md:h-10',
                period === p.key
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 max-md:w-full">
            <label className="inline-flex items-center gap-1.5 text-caption text-neutral-600 max-md:w-full">
              <span className="shrink-0 max-md:w-10">From</span>
              <Input type="date" value={customFrom} max={customTo || undefined}
                     onChange={(e) => set({ from: e.target.value || undefined })} className="h-10 w-full sm:w-40" />
            </label>
            <label className="inline-flex items-center gap-1.5 text-caption text-neutral-600 max-md:w-full">
              <span className="shrink-0 max-md:w-10">To</span>
              <Input type="date" value={customTo} min={customFrom || undefined}
                     onChange={(e) => set({ to: e.target.value || undefined })} className="h-10 w-full sm:w-40" />
            </label>
          </div>
        )}
      </div>

      {/* ── How many, over that period ───────────────────────────── */}
      {summary.error ? (
        <ErrorState title="The totals could not be loaded" detail={summary.error} onRetry={summary.reload} />
      ) : (
        <KeyFigures
          label={`Treatments, ${periodLabel}`}
          figures={[
            { key: 'total', label: 'Total treatments', value: s?.total ?? 0, loading: !s, hint: periodLabel,
              onClick: () => clearFilters() },
            { key: 'casualty', label: 'Casualties', value: s?.byKind.CASUALTY ?? 0, loading: !s,
              tone: s?.byKind.CASUALTY ? 'overdue' : undefined, hint: 'Injuries, emergencies',
              onClick: () => set({ kind: 'CASUALTY', work: undefined }) },
            { key: 'firstAid', label: 'First aid', value: s?.byKind.FIRST_AID ?? 0, loading: !s,
              hint: 'Minor, treated on site', onClick: () => set({ kind: 'FIRST_AID', work: undefined }) },
            { key: 'opd', label: 'OPD visits', value: s?.byKind.OPD ?? 0, loading: !s,
              hint: 'Seen at the OHC', onClick: () => set({ kind: 'OPD', work: undefined }) },
            { key: 'work', label: 'Work-related', value: s?.workRelated ?? 0, loading: !s,
              tone: s?.workRelated ? 'dueSoon' : undefined, hint: 'Arose out of the work',
              onClick: () => set({ work: 'true', kind: undefined }) },
            // The list cannot be narrowed to pending follow-ups, so this one is a figure, not a link.
            { key: 'followup', label: 'Follow-ups due', value: s?.followUpsPending ?? 0, loading: !s,
              tone: s?.followUpsPending ? 'pending' : undefined, hint: 'Today or later' },
          ]}
        />
      )}

      <FilterBar
        filters={[
          ...(showPlant ? [{ key: 'plantId', label: 'Plant', allLabel: 'All my plants',
            options: plantList.map((p) => ({ value: p.id, label: p.name })) }] : []),
          { key: 'kind', label: 'Type', allLabel: 'All types',
            options: TREATMENT_KINDS.map((k) => ({ value: k, label: TREATMENT_KIND_LABEL[k] })) },
          { key: 'outcome', label: 'Outcome', allLabel: 'Any outcome',
            options: TREATMENT_OUTCOMES.map((o) => ({ value: o, label: TREATMENT_OUTCOME_LABEL[o] })) },
          { key: 'work', label: 'Work-related', allLabel: 'Work-related or not',
            options: [{ value: 'true', label: 'Work-related' }, { value: 'false', label: 'Not work-related' }] },
        ]}
        value={{ kind: kind || undefined, outcome: outcome || undefined, work: workRelated || undefined, plantId: plantId || undefined }}
        onChange={(v) => set({ kind: v.kind, outcome: v.outcome, work: v.work, plantId: v.plantId })}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          aria-label="Search treatments by employee name or ID"
          placeholder="Search employee name or ID"
          className="w-full sm:w-72"
        />
      </FilterBar>

      <DataTable<TreatmentRow>
        rows={page.items}
        loading={page.loading}
        error={page.error}
        onRetry={page.reload}
        hasMore={page.hasMore}
        onLoadMore={page.loadMore}
        loadingMore={page.loadingMore}
        rowKey={(t) => t.id}
        onRowClick={(t) => setOpenId(t.id)}
        empty={filtered ? {
          title: 'No treatment matches these filters',
          hint: 'Try another type or outcome, or a longer period.',
          action: <Button variant="secondary" onClick={clearFilters}>Clear filters</Button>,
        } : {
          title: period === 'all' ? 'No treatments recorded yet' : `Nothing recorded — ${periodLabel}`,
          hint: canWrite
            ? 'Record a visit to the first-aid room or OHC here, or from the employee\'s own page.'
            : 'Treatments recorded by the first-aid room or OHC appear here.',
          action: canWrite
            ? <Button onClick={() => setRecording(true)}>Record treatment</Button>
            : period !== 'all' ? <Button variant="secondary" onClick={() => set({ period: 'all', from: undefined, to: undefined })}>Show all time</Button> : undefined,
        }}
        columns={treatmentColumns({ withEmployee: true })}
      />

      {recording && (
        <TreatmentDialog
          onClose={() => setRecording(false)}
          onDone={(row) => { setRecording(false); setSaved(row); reload(); }}
        />
      )}
      {openId && (
        <TreatmentDetailDialog id={openId} onClose={() => setOpenId(null)} onChanged={() => { setSaved(null); reload(); }} />
      )}
    </div>
  );
}
